import { emptyState, normalizeUser } from '../storage';
import type { AppState } from '../../types';
import type { PersistenceProvider } from './types';

const API_BASE = import.meta.env.VITE_API_URL ?? '';

async function apiFetch(
  path: string,
  init?: RequestInit,
): Promise<Response> {
  const res = await fetch(`${API_BASE}${path}`, {
    ...init,
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...init?.headers,
    },
  });
  return res;
}

export class ApiPersistenceProvider implements PersistenceProvider {
  private authenticated = false;

  async hydrate(): Promise<AppState> {
    const res = await apiFetch('/api/state');
    if (!res.ok) {
      console.warn('Kunne ikke hente state fra server', res.status);
      return emptyState();
    }
    const data = (await res.json()) as {
      state: AppState;
      authenticated?: boolean;
    };
    this.authenticated = !!data.authenticated;
    return {
      version: 3.5,
      theme: data.state.theme === 'dark' ? 'dark' : 'light',
      activeUserId: data.state.activeUserId ?? null,
      users: data.state.users.map((u) => normalizeUser(u, 3.5)),
    };
  }

  async save(state: AppState): Promise<boolean> {
    const res = await apiFetch('/api/state', {
      method: 'PUT',
      body: JSON.stringify({ state }),
    });
    if (!res.ok) {
      console.warn('Kunne ikke gemme state', res.status);
      return false;
    }
    return true;
  }

  async register(email: string, password: string): Promise<void> {
    const res = await apiFetch('/api/auth/register', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    });
    if (!res.ok) {
      const err = (await res.json().catch(() => ({}))) as { error?: string };
      throw new Error(err.error ?? 'Registrering mislykkedes');
    }
    const data = (await res.json()) as { authenticated?: boolean };
    this.authenticated = !!data.authenticated;
  }

  async login(email: string, password: string): Promise<void> {
    const res = await apiFetch('/api/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    });
    if (!res.ok) {
      const err = (await res.json().catch(() => ({}))) as { error?: string };
      throw new Error(err.error ?? 'Login mislykkedes');
    }
    this.authenticated = true;
  }

  async logout(): Promise<void> {
    await apiFetch('/api/auth/logout', { method: 'POST' });
    this.authenticated = false;
  }

  async deleteAccount(password: string): Promise<void> {
    const res = await apiFetch('/api/auth/account', {
      method: 'DELETE',
      body: JSON.stringify({ password }),
    });
    if (!res.ok) {
      const err = (await res.json().catch(() => ({}))) as { error?: string };
      throw new Error(err.error ?? 'Kunne ikke slette konto');
    }
    this.authenticated = false;
  }

  isAuthenticated(): boolean {
    return this.authenticated;
  }
}
